import { useCallback, useEffect, useState } from 'react';
import { Loader2, TrendingUp, TrendingDown, Minus, AlertTriangle, ArrowRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import { tradeService } from '../services/trade-service';
import { formatFullDate } from '../utils/trade-dates';
import type { RequoteResult } from '../types/trade.types';

/**
 * Recotizar: la misma ruta, con los precios de hoy.
 *
 * El tarifario de ABCD se renueva **cada semana**, así que toda cotización de
 * más de siete días necesita recotizarse. Antes había que arrancar de cero:
 * buscar el cliente, la ruta, el equipo y el peso a mano.
 *
 * **Lo que da el valor es la comparación, no el número nuevo.** Un precio
 * distinto sin explicación obliga a Delicia a abrir las dos cotizaciones y
 * restar a mano, y a llamar al cliente sin poder justificarlo. Con "subió 330 el
 * marítimo" la llamada se hace sola.
 *
 * **No guarda nada al abrirse.** Recotizar automáticamente pisaría un precio
 * que quizá ya se le prometió al cliente; acá se mira primero y se decide
 * después.
 */

interface RequoteDialogProps {
  quoteId: string | null;
  onClose: () => void;
  /** Lleva al cotizador con todo cargado para guardar la nueva. */
  onContinue: (prefill: RequoteResult['prefill']) => void;
}

const money = (n: number, currency: string) =>
  `${currency} ${n.toLocaleString('es-BO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export function RequoteDialog({ quoteId, onClose, onContinue }: RequoteDialogProps) {
  const [data, setData] = useState<RequoteResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      setData(await tradeService.requote(id));
    } catch (err) {
      setError(
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ??
          'No se pudo recotizar.'
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (quoteId) void load(quoteId);
  }, [quoteId, load]);

  const comp = data?.comparison ?? null;
  const subio = comp !== null && comp.difference > 0;
  const bajo = comp !== null && comp.difference < 0;
  const igual = comp !== null && comp.difference === 0;

  return (
    <Dialog open={quoteId !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>
            Recotizar {data ? data.previous.orderNumber : ''} con los precios de hoy
          </DialogTitle>
          <DialogDescription>
            Se recalcula la misma ruta, el mismo equipo y el mismo peso. Todavía no se guarda nada.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-24 w-full" />
          </div>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-alerta-borde bg-alerta-suave px-3 py-2.5 text-sm text-foreground">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-alerta" />
            <span>{error}</span>
          </div>
        )}

        {data && !loading && (
          <div className="space-y-4">
            {/* ── Cuánto cambió, que es lo que se vino a ver ── */}
            {comp !== null ? (
              <div
                className={cn(
                  'rounded-xl border p-4',
                  subio && 'border-bloqueo-borde bg-bloqueo-suave ',
                  bajo && 'border-ok-borde bg-ok-suave ',
                  igual && 'border-border bg-muted/40'
                )}
              >
                <div className="flex flex-wrap items-center justify-center gap-3 font-mono text-lg tabular-nums">
                  <span className="text-muted-foreground line-through decoration-1">
                    {money(comp.previousTotal, comp.currency)}
                  </span>
                  <ArrowRight className="h-4 w-4 text-muted-foreground" aria-hidden />
                  <span className="font-semibold">{money(comp.currentTotal, comp.currency)}</span>
                </div>

                <p className="mt-2 flex items-center justify-center gap-1.5 text-sm font-medium">
                  {subio && <TrendingUp className="h-4 w-4 text-bloqueo" aria-hidden />}
                  {bajo && <TrendingDown className="h-4 w-4 text-ok" aria-hidden />}
                  {igual && <Minus className="h-4 w-4 text-muted-foreground" aria-hidden />}
                  {igual
                    ? 'El precio no cambió'
                    : `${subio ? 'Subió' : 'Bajó'} ${money(Math.abs(comp.difference), comp.currency)}` +
                      (comp.percent === null ? '' : ` (${Math.abs(comp.percent)} %)`)}
                </p>

                {comp.hasExpiredRates && (
                  <p className="mt-2 flex items-start gap-1.5 text-xs leading-relaxed text-alerta ">
                    <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                    El precio nuevo también se apoya en tarifas vencidas. Conviene subir el
                    tarifario de la semana antes de mandarlo.
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-lg border border-alerta-borde bg-alerta-suave px-3 py-2.5 text-sm text-foreground">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-alerta" />
                <span>
                  <strong>Hoy no hay ninguna tarifa para esa ruta.</strong> No es un error: el
                  tarifario dejó de cubrirla. Revisá el tarifario o cotizá a mano.
                </span>
              </div>
            )}

            {/* ── Contra qué se compara ── */}
            <div className="rounded-lg border">
              <div className="border-b bg-muted/40 px-3 py-2 text-xs font-medium text-muted-foreground">
                Lo cotizado el {formatFullDate(data.previous.quotedAt)} · vencía el{' '}
                {formatFullDate(data.previous.validUntil)}
              </div>
              <div className="divide-y">
                {data.previous.legs.map((leg, i) => (
                  <div
                    key={`${leg.origin}-${leg.destination}-${i}`}
                    className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs"
                  >
                    <span className="min-w-0 truncate">
                      {leg.origin} → {leg.destination}
                    </span>
                    <span className="shrink-0 font-mono tabular-nums text-muted-foreground">
                      {leg.price === null ? '—' : money(leg.price, data.previous.currency)}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {data.current.options.length > 1 && (
              <p className="text-xs leading-relaxed text-muted-foreground">
                Hoy hay <strong>{data.current.options.length} opciones</strong> para esa ruta.
                Arriba se compara contra la mejor; las demás se eligen en el cotizador.
              </p>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cerrar
          </Button>
          <Button
            disabled={loading || !data || data.current.options.length === 0}
            onClick={() => data && onContinue(data.prefill)}
          >
            {loading && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
            Seguir en el cotizador
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
